/**
 * WFA Event Service
 * 
 * Manages SSE connections for Walk Forward Analysis real-time updates.
 * Completely separate from optimization event service.
 */

// Helper to build full API endpoint URLs
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const buildEndpoint = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

export interface WFAEvent {
  type: 'wfa-connected' | 'wfa-progress' | 'wfa-completed' | 'wfa-failed' | 'wfa-queued' | 'wfa-error' | 'wfa-cancelled';
  jobId?: string;
  data?: WFAProgressData | WFACompletedData | WFAFailedData;
  timestamp?: string;
}

export interface WFAProgressData {
  current: number;
  total: number;
  progress: number;
  currentPeriod: number;
  totalPeriods: number;
  currentPhase: string;
  estimatedTimeRemaining?: number;
  error?: string;
  savedWfaId?: string;
}

export interface WFAPeriod {
  periodIndex: number;
  inSampleStart: string;
  inSampleEnd: string;
  outSampleStart: string;
  outSampleEnd: string;
  configuration: Record<string, unknown>;
}

export interface WFAPeriodResult {
  periodIndex: number;
  inSampleMetrics: Record<string, unknown>;
  outSampleMetrics: Record<string, unknown>;
  parameters: Record<string, unknown>;
}

export interface WFACompletedData {
  type?: 'wfa';
  savedWfaId?: string;  // The ID of the saved WFA result for navigation
  periods?: WFAPeriod[];
  summary?: {
    totalPeriods?: number;
    successfulPeriods?: number;
    avgInSampleReturn?: number;
    avgOutSampleReturn?: number;
    avgInSampleSharpe?: number;
    avgOutSampleSharpe?: number;
    winRate?: number;
    consistency?: number;
    robustness?: string;
    returnStandardDeviation?: number;
    overfittingMetrics?: {
      wfe: number;
      performanceDrop: number;
      sharpeRatioDrop: number;
      winRateDrop: number;
    };
    bestPeriod?: WFAPeriodResult;
    worstPeriod?: WFAPeriodResult;
  };
  periodResults?: WFAPeriodResult[];
  aggregatedMetrics?: Record<string, unknown>;
  totalPeriods?: number;
  successfulPeriods?: number;
  configuration?: {
    strategy: string;
    symbol: string;
    interval: string;
    analysisStartDate: string;
    analysisEndDate: string;
    inSamplePeriod: number;
    outSamplePeriod: number;
    stepSize: number;
  };
}

export interface WFAFailedData {
  error: string;
}

type WFAEventCallback = (event: WFAEvent) => void;

class WFAEventService {
  private eventSource: EventSource | null = null;
  private isConnected = false;
  private subscribers: Set<WFAEventCallback> = new Set();
  private jobSubscribers: Map<string, Set<WFAEventCallback>> = new Map();
  private connectionRetryCount = 0;
  private maxRetries = 5;
  private retryTimeoutId: NodeJS.Timeout | null = null;

  /**
   * Connect to WFA SSE endpoint
   */
  connect(): void {
    if (this.eventSource && 
        (this.eventSource.readyState === EventSource.OPEN || 
         this.eventSource.readyState === EventSource.CONNECTING)) {
      return;
    }

    if (this.eventSource) {
      this.disconnect();
    }
    
    try {
      const eventSourceUrl = buildEndpoint('/wfa/events');
      this.eventSource = new EventSource(eventSourceUrl, { withCredentials: true });
      
      this.eventSource.onopen = () => {
        this.isConnected = true;
        this.connectionRetryCount = 0;
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data: WFAEvent = JSON.parse(event.data);
          this.handleEvent(data);
        } catch (error) {
        }
      };

      this.eventSource.onerror = (error) => {
        this.isConnected = false;
        this.handleConnectionError();
      };

    } catch (error) {
      this.handleConnectionError();
    }
  }

  /**
   * Disconnect from SSE
   */
  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
    }
    
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    
    this.connectionRetryCount = 0;
  }

  /**
   * Handle connection errors with retry logic
   */
  private handleConnectionError(): void {
    if (this.connectionRetryCount >= this.maxRetries) {
      return;
    }

    this.connectionRetryCount++;
    const retryDelay = Math.min(1000 * Math.pow(2, this.connectionRetryCount), 30000);
    
    this.retryTimeoutId = setTimeout(() => {
      this.connect();
    }, retryDelay);
  }

  /**
   * Subscribe to WFA events globally (all jobs)
   */
  subscribeGlobal(callback: WFAEventCallback): () => void {
    this.subscribers.add(callback);
    
    // Auto-connect if not already connected
    if (!this.isConnected && !this.eventSource) {
      this.connect();
    }
    
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Subscribe to WFA events for a specific job
   */
  subscribe(jobIdOrCallback: string | WFAEventCallback, callback?: WFAEventCallback): () => void {
    // Legacy signature: subscribe(callback)
    if (typeof jobIdOrCallback === 'function') {
      return this.subscribeGlobal(jobIdOrCallback);
    }
    
    // New signature: subscribe(jobId, callback)
    const jobId = jobIdOrCallback;
    if (!callback) {
      return () => {};
    }
    
    if (!this.jobSubscribers.has(jobId)) {
      this.jobSubscribers.set(jobId, new Set());
    }
    this.jobSubscribers.get(jobId)!.add(callback);
    
    // Auto-connect if not already connected
    if (!this.isConnected && !this.eventSource) {
      this.connect();
    }
    
    return () => {
      const jobSubs = this.jobSubscribers.get(jobId);
      if (jobSubs) {
        jobSubs.delete(callback);
        if (jobSubs.size === 0) {
          this.jobSubscribers.delete(jobId);
        }
      }
    };
  }

  /**
   * Handle incoming WFA events
   */
  private handleEvent(event: WFAEvent): void {
    // Notify global subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[WFAEventService] Error in global subscriber:', error);
      }
    });
    
    // Notify job-specific subscribers
    if (event.jobId) {
      const jobSubs = this.jobSubscribers.get(event.jobId);
      if (jobSubs) {
        jobSubs.forEach(callback => {
          try {
            callback(event);
          } catch (error) {
            console.error('[WFAEventService] Error in job subscriber:', error);
          }
        });
      }
    }
  }

  /**
   * Check if connected to SSE
   */
  isConnectedToSSE(): boolean {
    return this.isConnected && this.eventSource?.readyState === EventSource.OPEN;
  }
}

export const wfaEventService = new WFAEventService();
export default wfaEventService;
